// /var/www/afra/web-design.rampagesoft.com/my-app/src/component/ContentDetail.js
// ContentDetail.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BreadCrumb } from 'primereact/breadcrumb';
import { ProgressSpinner } from 'primereact/progressspinner';
import { fetchPostImage, fetchPosts, fetchPostById, incrementVisitors } from '../services/api';
import MetaTagContentDetail from '../metatag/MetaTagContentDetail';
import Slidebar from '../component/slidebar.js';
import '../css/ContentDetail.scss';
import 'primeicons/primeicons.css';

// Utility functions
const stripHtml = html => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

const truncateText = (text, max) =>
  text.length <= max ? text : text.substring(0, max) + '...';

const createUrlFriendlyString = text =>
  encodeURIComponent(text)
    .replace(/%20/g, '-')
    .replace(/%[0-9A-F]{2}/g, match => (match === '%2F' ? '/' : match));

const formatDate = dateString => {
  const d = new Date(dateString);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const getImageUrl = (imgValue, defaultImage) => {
  if (!imgValue) return defaultImage;
  const cleanImgValue = imgValue.replace(/^\./, '').trim();
  if (cleanImgValue.startsWith('http://') || cleanImgValue.startsWith('https://')) {
    return cleanImgValue;
  }
  return `https://maracar.rampagesoft.com/model/upload/img/IMG_Home/${cleanImgValue}`;
};

export default function ContentDetail() {
  const { id } = useParams();
  const [content, setContent] = useState({
    Home_Sub: 'กำลังโหลด...',
    Home_Short: '',
    Home_Detail: 'กำลังโหลดเนื้อหา...',
    Home_Date: new Date().toISOString(),
    Home_Visitors: 0
  });
  const [imageUrl, setImageUrl] = useState('https://afaa.website/s/ff6970.png');
  const [slidebarData, setSlidebarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const heading = 'CONTENT IDEAS LAB';
  const defaultImage = 'https://afaa.website/s/ff6970.png';

  useEffect(() => {
    async function fetchContentDetail() {
      try {
        setLoading(true);
        setError(null);

        // Fetch post by ID directly
        const data = await fetchPostById(id);
        const post = data.results?.[0];
        if (!post) throw new Error(`No post found for ID: ${id}`);
        //console.log('Fetched post:', post);

        // Set default Home_Sub if missing
        const finalPost = {
          ...post,
          Home_Sub: post.Home_Sub || 'Untitled Article'
        };
        if (!post.Home_Sub) {
          console.warn(`Warning: Home_Sub is missing for post ID ${id}. Using default "Untitled Article"`);
        }

        // Fetch raw image field
        let rawImg = '';
        try {
          const imageData = await fetchPostImage(id);
          rawImg = imageData.results?.[0]?.ImgHome_Img || '';
          //console.log('Raw image:', rawImg);
        } catch (e) {
          console.warn(`Failed to fetch image for ID ${id}: ${e.message}`);
        }
        const finalUrl = getImageUrl(rawImg, defaultImage);
        //console.log('Final image URL:', finalUrl);

        setContent(finalPost);
        setImageUrl(finalUrl);

        // Increment visitors
        try {
          await incrementVisitors(id);
        } catch (e) {
          console.warn(`Failed to increment visitors for ID ${id}: ${e.message}`);
        }

        // Fetch Slidebar data
        const resp = await fetchPosts(10);
        const items = await Promise.all(
          (resp.results || []).filter(p => p.Home_ID && p.Home_Sub).map(async p => {
            let raw = '';
            try {
              const imgData = await fetchPostImage(p.Home_ID);
              raw = imgData.results?.[0]?.ImgHome_Img || '';
            } catch (e) {
              console.warn(`Failed to fetch slidebar image for Home_ID ${p.Home_ID}: ${e.message}`);
            }
            const url = getImageUrl(raw, defaultImage);
            const title = truncateText(stripHtml(p.Home_Sub || ''), 50);
            const desc = truncateText(stripHtml(p.Home_Short || ''), 200);
            const link = `/content/${p.Home_ID}/${createUrlFriendlyString(stripHtml(p.Home_Sub || ''))}`;
            return { id: p.Home_ID, img: url, title, description: desc, link, namelink: 'Read More', style: {} };
          })
        );
        setSlidebarData(items);
      } catch (e) {
        console.error('Fetch content error:', e.message);
        if (e.message.includes('Failed to fetch')) {
          setError('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่อหรือลองใหม่ภายหลัง');
        } else {
          setError(`ไม่สามารถโหลดเนื้อหาได้: ${e.message}`);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchContentDetail();
  }, [id]);

  const breadcrumbItems = [
    { label: 'Home', url: '/', icon: 'pi pi-home' },
    { label: 'Content', url: '/content', icon: 'pi pi-folder' },
    { label: truncateText(stripHtml(content.Home_Sub || 'Untitled'), 50), disabled: true }
  ];

  const currentUrl = `${window.location.origin}/content/${id}/${createUrlFriendlyString(stripHtml(content.Home_Sub || 'Untitled'))}`;

  return (
    <section>
      <MetaTagContentDetail
        content={content}
        currentUrl={currentUrl}
        imageUrl={imageUrl}
      />
      <div className="container">
        <BreadCrumb model={breadcrumbItems} className="content-detail__breadcrumb" />
        {loading ? (
          <div className="content-detail__loading"><ProgressSpinner /></div>
        ) : error ? (
          <div className="content-detail__error">{error}</div>
        ) : (
          <article className="content-detail">
            <header className="content-detail__header">
              <h1 className="content-detail__title">{content.Home_Sub}</h1>
              <div className="content-detail__meta">
                <span className="content-detail__meta-item">
                  <i className="pi pi-calendar" /> {formatDate(content.Home_Date || new Date())}
                </span>
                <span className="content-detail__meta-item">
                  <i className="pi pi-eye" /> {content.Home_Visitors || 0}
                </span>
              </div>
            </header>
            {imageUrl && (
              <figure className="content-detail__image-container">
                <img
                  src={imageUrl}
                  alt={content.Home_Sub || 'Content Image'}
                  className="content-detail__image"
                  onError={() => console.warn(`Failed to load image: ${imageUrl}`)}
                />
              </figure>
            )}
            <section className="content-detail__details">
              <div dangerouslySetInnerHTML={{ __html: content.Home_Detail || 'No content available' }} />
            </section>
          </article>
        )}
      </div>
      <Slidebar data={slidebarData} heading={heading} theme="primary-home" />
    </section>
  );
}